import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowUUpLeft, WarningCircle, Package } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { devApi } from './devApi';

/**
 * 完了を取り消す（管理者）。
 * 完了は「案件のステージ」と「台帳への登録」を同時に行うので、取り消しも2つを別に聞く:
 *   ・どの段階に戻すか（完了以外のどこでも選べる）
 *   ・完了時に登録した台帳の行を 残す / 取り消す（他で使われていたら取り消せない）
 * 理由は必須。監査ログと進行履歴の両方に残る。
 */
export const DevRollbackDialog = ({ open, onOpenChange, devId, onDone }) => {
  const [info, setInfo] = useState(null);
  const [stage, setStage] = useState('');
  const [ledger, setLedger] = useState('keep');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason(''); setLedger('keep'); setInfo(null);
    devApi.rollbackPreview(devId)
      .then((d) => { setInfo(d); setStage(d.default_stage || ''); })
      .catch((e) => toast.error(e.response?.data?.detail || '取り消しの情報を取得できませんでした'));
  }, [open, devId]);

  const canRemove = !!info?.ledger?.can_remove;
  const ok = stage && reason.trim().length >= 3 && !(ledger === 'remove' && !canRemove);

  const run = async () => {
    setBusy(true);
    try {
      const r = await devApi.rollback(devId, {
        to_stage: stage, ledger_action: info?.ledger ? ledger : 'keep', reason: reason.trim(),
      });
      toast.success(`完了を取り消しました（${r.to_stage_label} に戻しました）`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : det?.message || '取り消せませんでした');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="dev-rollback-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base text-amber-800">
            <ArrowUUpLeft size={18} /> 完了を取り消す（管理者）
          </DialogTitle>
          <DialogDescription className="text-sm">
            完了の記録（完了日・登録先）を外して、選んだ段階に戻します。
            試作・検証・改訂・部品・書類の履歴は消えません。
          </DialogDescription>
        </DialogHeader>

        {!info ? (
          <p className="py-6 text-center text-sm text-zinc-500" data-testid="dev-rollback-loading">
            読み込み中…
          </p>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm"
              data-testid="dev-rollback-current">
              <p className="font-semibold text-zinc-800">{info.title}</p>
              <p className="text-zinc-600">
                完了日 {info.completed_at?.slice(0, 10) || '—'} ／ {info.final_outcome_label || '登録先なし'}
              </p>
            </div>

            <div>
              <Label className="text-xs font-semibold text-zinc-700">どの段階に戻しますか</Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger className="mt-1" data-testid="dev-rollback-stage-select">
                  <SelectValue placeholder="段階を選ぶ" />
                </SelectTrigger>
                <SelectContent>
                  {(info.stages || []).map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`dev-rollback-stage-${s.id}`}>
                      {s.label}{s.is_default ? '（完了の直前）' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {info.ledger && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-3"
                data-testid="dev-rollback-ledger">
                <p className="flex items-center gap-2 text-sm font-bold text-amber-900">
                  <Package size={15} /> 完了時に登録した行
                </p>
                <p className="mt-1 text-sm text-amber-900">
                  {info.ledger.type_label}: {info.ledger.no} {info.ledger.label}
                </p>
                <div className="mt-3 space-y-2">
                  <label className="flex items-start gap-2 text-sm text-zinc-800">
                    <input type="radio" className="mt-1" checked={ledger === 'keep'}
                      onChange={() => setLedger('keep')} data-testid="dev-rollback-keep-radio" />
                    <span>
                      <span className="font-semibold">残す</span>
                      <span className="block text-xs text-zinc-600">
                        台帳の行はそのまま。案件だけ再開します（案件との自動リンクは外れます）。
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-sm text-zinc-800">
                    <input type="radio" className="mt-1" checked={ledger === 'remove'}
                      disabled={!canRemove} onChange={() => setLedger('remove')}
                      data-testid="dev-rollback-remove-radio" />
                    <span>
                      <span className="font-semibold">取り消す</span>
                      <span className="block text-xs text-zinc-600">
                        {canRemove
                          ? `次のものが消えます: ${(info.ledger.removes || []).join(' / ')}`
                          : '他で使われているため取り消せません（下の理由を参照）'}
                      </span>
                    </span>
                  </label>
                </div>
                {!canRemove && (
                  <ul className="mt-2 space-y-1" data-testid="dev-rollback-blockers">
                    {(info.ledger.blockers || []).map((b, i) => (
                      <li key={i} className="flex items-start gap-1 text-xs text-amber-900">
                        <WarningCircle size={13} className="mt-0.5 shrink-0" />
                        <span>{b.label}{b.detail ? ` — ${b.detail}` : ''}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div>
              <Label className="text-xs font-semibold text-zinc-700">理由（必須）</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                className="mt-1" placeholder="例: 検証結果が未記録のまま完了したため差し戻す"
                data-testid="dev-rollback-reason-input" />
              <p className="mt-1 text-xs text-zinc-500">監査ログと進行履歴に残ります。</p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
            data-testid="dev-rollback-cancel-btn">やめる</Button>
          <Button disabled={!ok || busy} onClick={run}
            className="bg-amber-700 hover:bg-amber-800"
            data-testid="dev-rollback-submit-btn">
            {busy ? '処理中…' : '完了を取り消す'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
